const LOCAL_API_BASE_URL = 'http://localhost:3001';

function normalizeBaseUrl(value: string): string {
  return value.replace(/\/$/, '');
}

function isLocalDevHostname(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
}

function readConfiguredPublicApiUrl(): string | undefined {
  const expoPublic = process.env.EXPO_PUBLIC_API_URL?.trim();
  if (expoPublic) return expoPublic;
  const nextPublic = process.env.NEXT_PUBLIC_API_URL?.trim();
  if (nextPublic) return nextPublic;
  return undefined;
}

function isReactNativeRuntime(): boolean {
  if (typeof navigator !== 'undefined' && navigator.product === 'ReactNative') {
    return true;
  }
  // Expo / RN often define window without a real browser location usable as API origin.
  if (typeof window !== 'undefined') {
    const origin = window.location?.origin?.trim() ?? '';
    // Metro bundler origins must never be treated as the API host.
    if (/^https?:\/\/(localhost|127\.0\.0\.1|\[::1\])(:\d+)?$/i.test(origin)) {
      const port = (() => {
        try {
          return window.location?.port;
        } catch {
          return '';
        }
      })();
      // Next.js local apps use 3000/3002/3003; Metro commonly uses 8081/8082.
      if (port === '8081' || port === '8082' || port === '19000' || port === '19006') {
        return true;
      }
    }
  }
  return false;
}

/**
 * Resolves the API origin used for HTTP calls.
 *
 * Browser clients use the current app origin on deployed hosts so the edge proxy
 * can serve `/api/*` same-origin (BFF). Local web uses the configured API URL
 * because Next.js rewrites do not forward auth Set-Cookie reliably.
 *
 * Native / Expo clients must never use the Metro bundler origin — they use
 * `EXPO_PUBLIC_API_URL` (preferred) or `NEXT_PUBLIC_API_URL`.
 *
 * Server-side code uses the internal Docker URL when available.
 */
export function resolveApiBaseUrl(): string {
  if (isReactNativeRuntime()) {
    const configured = readConfiguredPublicApiUrl();
    if (configured) return normalizeBaseUrl(configured);
    return LOCAL_API_BASE_URL;
  }

  if (typeof window !== 'undefined') {
    const browserOrigin = window.location?.origin?.trim();
    if (browserOrigin) {
      const hostname = window.location?.hostname?.trim() ?? '';
      if (isLocalDevHostname(hostname)) {
        const configured = readConfiguredPublicApiUrl();
        if (configured) return normalizeBaseUrl(configured);
      }
      return normalizeBaseUrl(browserOrigin);
    }
  }

  const internal = process.env.API_INTERNAL_URL?.trim();
  if (internal) return normalizeBaseUrl(internal);

  const configured = readConfiguredPublicApiUrl();
  if (configured) return normalizeBaseUrl(configured);

  return LOCAL_API_BASE_URL;
}
